"use client";

import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, PenTool, Cog, Factory } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
const steps = [{
  title: "Strategy & Design",
  description: "We collaborate to select your target neighborhood and craft a high-impact, custom ad that captures the attention of Metro Atlanta's most affluent homeowners.",
  icon: MessageSquare
}, {
  title: "Premium Printing",
  description: "Your brand is brought to life on oversized, heavy-duty cardstock with a luxury finish, ensuring your business stands out the moment it's pulled from the mailbox.",
  icon: PenTool
}, {
  title: "USPS Sorting & Prep",
  description: "We handle the complex logistics of Every Door Direct Mail (EDDM), managing all USPS documentation and sorting to ensure precise delivery to your chosen routes.",
  icon: Cog
}, {
  title: "Doorstep Delivery",
  description: "Your message is delivered directly into the hands of 2,500+ households, providing local businesses with unparalleled visibility and neighborhood authority.",
  icon: Factory
}];
export default function CooperationProcess() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height - windowHeight;
      const scrolledDistance = -rect.top;
      if (totalScrollableDistance > 0) {
        let percentage = scrolledDistance / totalScrollableDistance * 100;
        percentage = Math.max(0, Math.min(100, percentage));
        setProgress(percentage);
      } else {
        if (rect.top <= 0) {
          setProgress(100);
        } else {
          setProgress(0);
        }
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return <section data-section-id="224221" id="the-design-to-door-journey" ref={sectionRef} className="relative bg-background text-foreground py-16 md:py-32">
      {}
      <div className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-xl border-b border-border shadow-sm py-4 mb-12">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl flex items-center gap-4">
          <span className="text-sm font-bold font-default text-secondary whitespace-nowrap uppercase tracking-widest">
            Campaign Progress
          </span>
          <Progress value={progress} className="h-2 flex-1 bg-muted" />
          <span className="text-sm font-bold text-secondary tabular-nums w-12 text-right font-default">
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold font-default text-secondary mb-4 tracking-tight">
            The Design-to-Door Journey
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default leading-relaxed">
            A seamless, full-service approach to placing your business in the most prestigious mailboxes across Metro Atlanta.
          </p>
        </div>

        <div className="relative">
          {}
          <div className="absolute left-6 md:left-8 top-4 bottom-4 w-0.5 bg-secondary/10 rounded-full hidden sm:block" />

          <div className="space-y-12 md:space-y-24">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="relative flex flex-col sm:flex-row items-start gap-6 md:gap-12 group">
                  {}
                  <div className="relative z-10 flex-shrink-0 flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-background border-2 border-border group-hover:border-primary transition-all duration-500 shadow-sm sm:ml-0 ml-4">
                    <Icon className="w-5 h-5 md:w-7 md:h-7 text-secondary group-hover:text-primary transition-colors duration-500" />
                  </div>

                  {}
                  <Card className="flex-1 w-full border-border bg-card shadow-lg hover:shadow-xl transition-all duration-500 rounded-md overflow-hidden">
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold text-primary-foreground bg-primary px-3 py-1 rounded-sm font-default uppercase tracking-wider">
                          Phase 0{i + 1}
                        </span>
                      </div>
                      <CardTitle className="text-xl md:text-2xl font-bold font-default text-secondary">
                        {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base text-muted-foreground font-default leading-relaxed">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}